/**
 * 認証状態管理 Slice
 *
 * 【このファイルの役割】
 * Redux Store で「今ログインしているユーザーは誰か」を管理する。
 *
 * 【なぜ Redux Store に持つのか？（AuthContext の useState ではダメ？）】
 * - AuthContext の useState は React コンポーネント内でしか使えない
 * - しかし baseQueryWithReauth（HTTP 通信の基盤）は React の外で動く
 * - リフレッシュ失敗時に `api.dispatch(logout())` でログアウトしたいが、
 *   useState では dispatch できない
 * - Redux Store なら React の外からでも dispatch できる
 *
 * 【2つのアクション】
 * - setUser: ログイン成功時 / getMe 成功時にユーザー情報をセット
 * - logout: ログアウト時 / リフレッシュ失敗時にユーザー情報をクリア
 *
 * 【AuthContext との関係】
 * AuthContext は Redux Store のデータを読み取って配信する「窓口」になる。
 * データの実体は Redux Store に、配信は AuthContext に、と役割分担する。
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/** Redux Store に保存するユーザー情報 */
interface AuthUser {
  id: number;
  code: string;
  name: string;
  role: string | null;
  permission_level: number;
  admin_level: number;
  must_change_password: boolean;
  privacy_consent_at: string | null;
}

/** 認証状態の型 */
interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** ログアウト直後フラグ — useGetMeQuery の再実行を防止する */
  isLoggedOut: boolean;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoggedOut: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * ユーザー情報をセットし、認証済み状態にする。
     * ログイン成功時、getMe 成功時に呼ばれる。
     */
    setUser(state, action: PayloadAction<AuthUser>) {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.isLoggedOut = false;
    },
    /**
     * ユーザー情報をクリアし、未認証状態にする。
     * ログアウト時、リフレッシュ失敗時に呼ばれる。
     */
    logout(state) {
      state.user = null;
      state.isAuthenticated = false;
      state.isLoggedOut = true;
    },
  },
});

export const { setUser, logout } = authSlice.actions;
export default authSlice.reducer;
